import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import { PromotionTile } from "@arctheme-components/ecommerce/PromotionalTile/PromotionTile";
import { CollectionSidebar } from "./CollectionSidebar";
import { CollectionToolbar } from "./CollectionToolbar";

import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { getSearchProvider } from "@src/providers";
import { useSearch } from "@src/contexts/SearchProvider";
import { DosagePopup } from "@project-stories/sections/ListProducts/DosagePopup";
import { SubscribePopup } from "@project-stories/sections/ListProducts/SubscribePopup";
import { SecondaryCTA } from "@project-stories/sections/ListProducts/ListProducts";

export const Collection = ({ settings, collectionProducts = [] }) => {
  const [products, setProducts] = useState([]);
  const [tabProducts, setTabProducts] = useState({});
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const { globals: { collection_tabs } } = settings;
  const requestedTabsRef = useRef({});
  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
  } = settings.section;

  // Extract specific block settings
  const filterButtonSettings = settings.filter_button_settings;
  const sortDropdownSettings = settings.sort_dropdown_settings;

  const currentHandle = settings.collection.handle;

  
  const mergeProductsWithBundles = (productsToMerge) => {
    if (!settings.products || !Array.isArray(productsToMerge)) return productsToMerge || [];
    
    return productsToMerge.map(product => {
      if (!product || !product.id) return product;
      
  
      const matchingProduct = settings.products.find(
        settingsProduct => settingsProduct && settingsProduct.id === product.id.toString()
      );
      
     
      if (matchingProduct && matchingProduct.bundle) {
        return {
          ...product,
          bundle: matchingProduct.bundle
        };
      }
      
      return product;
    });
  };

  useEffect(() => {
    if (collectionProducts?.length > 0) {
  
      const mergedProducts = mergeProductsWithBundles(collectionProducts);
      setProducts(mergedProducts);
    }
    
    const handleFiltersResults = (event) => {
      if (event.detail.type === "plp") {
        if (event.detail.filters) {
          setFilters(event.detail.filters);
        }
      }
    };
    const handleProductsResults = (event) => {
      if (event.detail.type === "plp") {
        window.dispatchEvent(
          new CustomEvent("hide-spinner", {
            detail: { id: "spinner-collection" }
          })
        );
        
        if (event.detail.handle === currentHandle || !event.detail.handle) {
         
          const mergedProducts = mergeProductsWithBundles(event.detail.products);
          setProducts(mergedProducts);
          
          if (collection_tabs?.length) {
            const otherTabs = collection_tabs.filter(tab => {
              const handle = tab.link.split('/').filter(Boolean).pop();
              return handle && handle !== currentHandle;
            });
            
            otherTabs.forEach((tab, index) => {
              const handle = tab.link.split('/').filter(Boolean).pop();
            
              if (requestedTabsRef.current[handle]) return;
              
              setTimeout(() => {
                requestedTabsRef.current[handle] = true;
                window.dispatchEvent(
                  new CustomEvent("searchrequest", {
                    detail: {
                      type: "plp",
                      collection: handle,
                      collectionSize: settings.collection_size,
                      isOtherCollection: true, 
                      filters_object: {}
                    },
                  })
                );
              }, index * 500);
            });
          }
        } else if (event.detail.handle && event.detail.handle !== currentHandle) {
          // Merge tab products with bundle data before setting state
          const mergedTabProducts = mergeProductsWithBundles(event.detail.products);
          
          setTabProducts(prev => ({
            ...prev,
            [event.detail.handle]: mergedTabProducts
          }));
        }
      }
    };


    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setSidebarVisible(false);
      }
    };

    // Set up event listeners
    window.addEventListener("product-list", handleProductsResults);
    window.addEventListener("filter-list", handleFiltersResults);
    window.addEventListener("keydown", handleEscapeKey);

    // Initialize the collection search when component mounts
    window.dispatchEvent(
      new CustomEvent("searchrequest", {
        detail: {
          type: "plp",
          collection: settings.collection.handle,
          collectionSize: settings.collection_size,
          filters_object: {}
        },
      })
    );

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener("product-list", handleProductsResults);
      window.removeEventListener("filter-list", handleFiltersResults);
      window.removeEventListener("keydown", handleEscapeKey);
    };
  }, [settings.collection.handle, settings.collection_size]);


  // Determine which search provider to use
  const SearchProviderComponent = getSearchProvider(settings.search_system);

  return (
    <SearchProviderComponent settings={settings} searchType="plp">
      <CollectionWithProvider
        products={products}
        tabProducts={tabProducts}
        currentHandle={currentHandle}
        filters={filters}
        settings={settings}
        isMobile={isMobile}
        isSidebarVisible={isSidebarVisible}
        toggleSidebar={toggleSidebar}
        filterButtonSettings={filterButtonSettings} // Pass down
        sortDropdownSettings={sortDropdownSettings} // Pass down
        columns={{ 
          mobile: columns_mobile, 
          desktop: columns_desktop,
          gapMobile: column_gap_mobile,
          gapDesktop: column_gap_desktop,
          rowGapMobile: row_gap_mobile,
          rowGapDesktop: row_gap_desktop
        }}
        id={id}
      />
    </SearchProviderComponent>
  );
};

// Internal component that uses the search provider context
function CollectionWithProvider({ 
  products, 
  tabProducts,
  filters,
  currentHandle,
  settings, 
  isMobile, 
  isSidebarVisible, 
  toggleSidebar,
  filterButtonSettings, // Receive prop
  sortDropdownSettings, // Receive prop
  columns,
  id
}) {
  // Access the search provider context
  const search = useSearch();
  
  // Add state for subscribe and dosage popups
  const [isOpen, setIsOpen] = useState(false);
  const [dosageGuide, setDosageGuide] = useState(false);
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [subscribeProduct, setSubscribeProduct] = useState(null);

 
  const secondaryCTA = settings.section?.secondary_cta_type || SecondaryCTA.DOSAGE;

  const onClickDosage = (productItem) => {
      const dosage = productItem?.dosage || productItem?.dosage_form;
      if(dosage) {
        setDosageGuide(productItem);
        setIsOpen(true);
      }
  };

  const onClickSubscribe = (productItem) => {
    setSubscribeProduct(productItem);
    setIsSubscribeOpen(true);
  };
  return (
    <div data-search-provider={search.settings.search_system}>
      <CollectionSidebar
        isVisible={isSidebarVisible}
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
      />
      
      <div className="collection__toolbar flex justify-between mb-4 items-center">
        <div className="flex w-full gap-2 items-center">
          {!settings.section.hide_filter_sort && (
            <CollectionToolbar
              settings={settings}
              toggleSidebar={toggleSidebar}
              filterButtonSettings={filterButtonSettings}
              sortDropdownSettings={sortDropdownSettings}
            />
          )}
        </div>
      </div>
      
      <div
        id="product-grid"
        className={`collection__product-grid relative grid grid-cols-${columns.mobile} lg:grid-cols-${columns.desktop} gap-x-[${columns.gapMobile}px] lg:gap-x-[${columns.gapDesktop}px] gap-y-[${columns.rowGapMobile}px] lg:gap-y-[${columns.rowGapDesktop}px] auto-cols-max`}
        data-id={id}
      >
    

        {settings.promotion_blocks?.map((promotion, index) => (
          <PromotionTile key={index} promotion={promotion} settings={settings} />
        ))}
        
        {products.map((product) => {
          return (
            <div
              key={product.id}
              className={"collection-tile"}
            >
              <ProductTile
                settings={settings}
                product={product}
                siblings={undefined}
                onClickDosage={() => onClickDosage(product)}
                showDosageButton={secondaryCTA == SecondaryCTA.DOSAGE && ( product?.dosage  || product.dosage_form )}
                onClickSubscribe={() => onClickSubscribe(product)}
                showSubscribeButton={secondaryCTA == SecondaryCTA.SUBSCRIBE}
                noSecondaryButton={secondaryCTA == SecondaryCTA.NONE}
              />
            </div>
          )
        })}

        
      </div>

      {Object.entries(tabProducts)
  .filter(([handle]) => handle !== currentHandle)
  .map(([handle, products]) => {
    // Find the tab info for title
    const tab = settings.globals.collection_tabs.find(tab =>
      tab.link.split('/').filter(Boolean).pop() === handle
    );
    return (
      <div key={handle} className="flex flex-col">
        <div className="h3 mb-4 lg:mb-5  mt-[32px] lg:mt-[36px]">{tab?.title || handle}</div>
        <div
          className={`collection__product-grid relative grid grid-cols-${columns.mobile} lg:grid-cols-${columns.desktop} gap-x-[${columns.gapMobile}px] lg:gap-x-[${columns.gapDesktop}px] gap-y-[${columns.rowGapMobile}px] lg:gap-y-[${columns.rowGapDesktop}px] auto-cols-max`}
          data-id={id}
        >
          {products.map((product) => (
            <div key={product.id} className="collection-tile">
              <ProductTile
                settings={settings}
                product={product}
                siblings={undefined}
                onClickDosage={() => onClickDosage(product)}
                showDosageButton={secondaryCTA == SecondaryCTA.DOSAGE  && (!!product?.dosage || !!product?.dosage_form)}
                onClickSubscribe={() => onClickSubscribe(product)}
                showSubscribeButton={secondaryCTA == SecondaryCTA.SUBSCRIBE}
              />
            </div>
          ))}
        </div>
      </div>
    );
  })}


      {/* Add Dosage and Subscribe Popups */}
      <DosagePopup isOpen={isOpen} setIsOpen={setIsOpen} product={dosageGuide} settings={settings} />
      {isSubscribeOpen && (
        <SubscribePopup
          isOpen={isSubscribeOpen}
          setIsOpen={setIsSubscribeOpen}
          product={subscribeProduct}
          settings={settings}
          translations={settings.translations}
        />
      )}
    </div>
  );
}
